import { useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  createAttachment,
  updateStatusCloseDetails,
  catalogByPart,
} from "../../../../../apis";
import Icon from "../../../../Icon/Icon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import EGFileUpload from "../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { SubmitButtons } from "./CloseReport";
import { CommentFiles } from "./CommentFiles";

const UNFOUNDED_CATALOGUE = "CS-200";

const Anothercauses = ({ anotherCauses, setAnotherCauses }) => {
  const theme = useTheme();

  useEffect(() => {
    return () => {
      setAnotherCauses([""]);
    };
  }, []);

  const handleChange = (value, idx) => {
    const newCauses = _.cloneDeep(anotherCauses);
    newCauses[idx] = value;
    setAnotherCauses(newCauses);
  };

  const handleAddCause = () => {
    const newCauses = _.cloneDeep(anotherCauses);
    newCauses.push("");
    setAnotherCauses(newCauses);
  };

  return (
    <Stack spacing={2} marginTop={2}>
      {anotherCauses.map((item, idx) => (
        <Stack key={idx} direction="row" alignItems="center">
          <EGInput
            label="Ingresa una nueva causa"
            placeholder="Escribe aquí"
            required={true}
            value={item}
            showHelpText={false}
            showSuccessValidation
            onChange={(e) => handleChange(e.target.value, idx)}
            isValid={item.trim() === "" ? false : true}
            autoComplete="off"
          />
          {idx === anotherCauses.length - 1 && (
            <Tooltip title="Añadir" arrow>
              <Box onClick={handleAddCause} marginLeft={2}>
                <Icon
                  name="add_square"
                  size={24}
                  color={theme.palette.ethicsBlue.main}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export const ClosedUnfounded = ({
  id,
  reportDetail,
  closeDate,
  reportExpired,
  reportRender,
  editClosed,
  setEditClosed,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const lang = localStorage.getItem("lang") || "es";

  const [observations, setObservations] = useState(
    reportDetail?.status?.details?.observations || ""
  );
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(
    reportDetail?.status?.details?.files || []
  );
  const [isLoading, setIsLoading] = useState(false);

  const [causes, setCauses] = useState(
    reportDetail?.status?.details?.causes || []
  );
  const [anotherCauses, setAnotherCauses] = useState(
    reportDetail?.status?.details?.another_causes?.length > 0
      ? reportDetail.status.details.another_causes
      : [""]
  );

  // Catalog state for unfounded close causes
  const [catalogOpts, setCatalogOpts] = useState(null);
  const useCatalog = Array.isArray(catalogOpts);

  // Consultar catálogo de causas de cierre no fundamentado
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: false,
          catalogue: UNFOUNDED_CATALOGUE,
          path: "/",
        });
        if (!resp.error && Array.isArray(resp.data) && resp.data.length > 0) {
          const capitalize = (str) =>
            str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
          setCatalogOpts(
            resp.data.map((item) => ({
              label: capitalize(item.locales?.[lang] || item.label),
              value: item.path_locales,
              locales: item.locales || {},
            }))
          );
        }
      } catch {
        // Si falla, se usan las opciones hardcodeadas
      }
    };
    fetchCatalog();
  }, []);

  const getCauseValue = (c) =>
    typeof c === "object" && c !== null ? c.value : c;

  const isAnotherSelected = causes.some(
    (c) => getCauseValue(c) === "another"
  );

  const returnIsValid = () => {
    if (
      isAnotherSelected &&
      anotherCauses?.findIndex((item) => item.trim() === "") >= 0
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);

    let files_id;
    let filesToSend = [];

    enqueueSnackbar(`${t("Conclusion.sending")}...`, { variant: "info" });

    try {
      if (files.length > 0) {
        for (const fileObj of files) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData);
          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            ...(!!fileObj.comment && { comment: fileObj.comment }),
          });
        }
      }

      // Add current files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          filesToSend.push({
            file: file.file,
            name: file.name,
            ...(!!file.comment && { comment: file.comment }),
          });
        });
      }

      const details = {
        another_causes: anotherCauses[0] === "" ? [] : anotherCauses,
        followup: false,
        closed: "unfounded",
        close_date: closeDate,
        causes: causes,
        observations,
        files: filesToSend,
        expired_reports: reportExpired,
      };

      await updateStatusCloseDetails(id, details);
      enqueueSnackbar(`${t("Conclusion.sending_successful")}`, {
        variant: "success",
      });
      reportRender();
      setIsLoading(false);
      setEditClosed(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  // Opciones legacy hardcodeadas (usadas si no hay catálogo)
  const legacyOptions = [
    {
      value: "incomplete_information",
      label: t("Conclusion.incomplete_information"),
    },
    { value: "false_information", label: t("Conclusion.false_information") },
    {
      value: "insufficient_evidence_found",
      label: t("Conclusion.insufficient_evidence_found"),
    },
    { value: "another", label: t("Conclusion.another") },
  ];

  // Opción "Otra" extra para mantener el flujo de causas libres con catálogo
  const ANOTHER_OPTION = {
    value: "another",
    label: t("Conclusion.another"),
    locales: {},
  };

  const options = useCatalog ? [...catalogOpts, ANOTHER_OPTION] : legacyOptions;

  const selectedOptions = options.filter((opt) =>
    causes.some((c) => getCauseValue(c) === opt.value)
  );

  const handleSelectChange = (_e, selecteds) => {
    if (useCatalog) {
      setCauses(
        selecteds.map((opt) => ({
          value: opt.value,
          label: opt.label,
          locales: opt.locales || {},
        }))
      );
    } else {
      setCauses(selecteds.map((opt) => opt.value));
    }
  };

  return (
    <>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label}
        id="movie-customized-option-demo"
        disableCloseOnSelect
        disableClearable
        multiple
        value={selectedOptions}
        onChange={handleSelectChange}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.value}
              sx={{
                backgroundColor: theme.palette.blueGray100.main,
                fontSize: 12,
              }}
              size="small"
              {...getTagProps({ index })}
              label={
                <Typography variant="body1" mx={1}>
                  {option.label}
                </Typography>
              }
            />
          ))
        }
        renderOption={(props, option, { selected }) => {
          return (
            <Box
              key={option.value}
              {...props}
              sx={{
                maxHeight: 47,
                p: 2,
                my: 0.5,
                mx: 1,
                borderRadius: 1.5,
                border: `${theme.palette.blueGray50.main} 1px solid`,
              }}
            >
              <Checkbox size="small" checked={selected} />
              <Typography variant="body1" mx={1}>
                {option.label}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <EGInput
            label={t("Conclusion.unfounded_closed_causes")}
            placeholder={t("Conclusion.select_an_option")}
            {...params}
          />
        )}
      />

      {isAnotherSelected && (
        <Anothercauses
          anotherCauses={anotherCauses}
          setAnotherCauses={setAnotherCauses}
        />
      )}

      <EGInput
        label={t("Conclusion.observations_type_close")}
        placeholder={t("Conclusion.observations_type_close_placeholder")}
        multiline
        rows={4}
        value={observations}
        onChange={(e) => setObservations(e.target.value)}
        sx={{ marginY: 2 }}
      />

      <Box marginBottom={2}>
        <EGFileUpload
          title={t("Conclusion.support_fiels")}
          files={files}
          setFiles={setFiles}
          disabled={isLoading}
        />
      </Box>

      {(files.length > 0 || uploadedFiles.length > 0) && (
        <Stack
          border={`1px solid ${theme.palette.teal.main}`}
          borderRadius={1}
          marginBottom={2}
        >
          {files.length !== 0 && (
            <CommentFiles
              files={files}
              setFiles={setFiles}
              pathName="file.name"
              pathSize="file.size"
            />
          )}

          {uploadedFiles.length !== 0 && (
            <CommentFiles files={uploadedFiles} setFiles={setUploadedFiles} />
          )}
        </Stack>
      )}

      <SubmitButtons
        isLoading={isLoading}
        disabledSave={!returnIsValid()}
        onSave={handleSubmit}
        onCancel={() => setEditClosed(false)}
        hideCancel={!editClosed}
      />
    </>
  );
};
